// frontend/src/context/PlatformSecurityCapabilityContext.jsx
// Platform Security capability context (ADR-SEC-001 / CES-COP-001B)
//
// Exposes the authenticated caller's own effective Platform Security
// capability codes to the frontend, sourced exclusively from
// platformSecurityCapabilityService.fetchEffectivePlatformSecurityCapabilities().
//
// This Provider is UX state only. It is not a security authority: every
// privileged server action must still independently re-verify capability
// authorization server-side (via user_has_platform_capability or an
// equivalent server-side check). Nothing here is ever persisted to
// localStorage or sessionStorage, and no route or navigation is added or
// modified by this slice.
//
// © 2026 Advanced Advisory Services, LLC. All Rights Reserved.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "../lib/supabaseClient";
import { fetchEffectivePlatformSecurityCapabilities } from "../lib/platformSecurityCapabilityService";

// Conservative UX-only staleness window: solely decides whether a
// TOKEN_REFRESHED auth event should trigger a re-fetch of the same
// identity's capabilities. Not a security boundary of any kind.
const STALE_MS = 120000;

const EMPTY_SET = new Set();

const PlatformSecurityCapabilityContext = createContext(null);

export function PlatformSecurityCapabilityProvider({ children }) {
  const [status, setStatus] = useState("idle");
  const [capabilities, setCapabilities] = useState(EMPTY_SET);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const userIdRef = useRef(null);
  const lastFetchedAtRef = useRef(null);

  const clearToUnauthenticated = useCallback(() => {
    // Invalidate any in-flight fetch so a late-arriving response from a
    // prior identity can never repopulate state after sign-out.
    requestIdRef.current += 1;
    userIdRef.current = null;
    lastFetchedAtRef.current = null;

    if (!mountedRef.current) return;
    setStatus("unauthenticated");
    setCapabilities(EMPTY_SET);
    setError(null);
  }, []);

  const resolveCapabilities = useCallback(async ({ force = false } = {}) => {
    const requestId = (requestIdRef.current += 1);

    let session = null;
    try {
      const { data } = await supabase.auth.getSession();
      session = data?.session || null;
    } catch {
      session = null;
    }

    if (!mountedRef.current || requestIdRef.current !== requestId) return;

    const nextUserId = session?.user?.id || null;

    if (!nextUserId) {
      userIdRef.current = null;
      lastFetchedAtRef.current = null;
      setStatus("unauthenticated");
      setCapabilities(EMPTY_SET);
      setError(null);
      return;
    }

    const identityChanged = nextUserId !== userIdRef.current;

    if (!identityChanged && !force) {
      const lastFetchedAt = lastFetchedAtRef.current;
      const isFresh =
        typeof lastFetchedAt === "number" &&
        Date.now() - lastFetchedAt < STALE_MS;
      if (isFresh) {
        // Existing snapshot is not stale; this is a no-op revalidation
        // (e.g. a TOKEN_REFRESHED event for the same identity).
        return;
      }
    }

    if (identityChanged) {
      // Never show a previous identity's capabilities while the new
      // identity's fetch is in flight.
      userIdRef.current = nextUserId;
      lastFetchedAtRef.current = null;
      setCapabilities(EMPTY_SET);
      setError(null);
    }

    setStatus("loading");

    const result = await fetchEffectivePlatformSecurityCapabilities();

    if (!mountedRef.current || requestIdRef.current !== requestId) return;
    // A newer identity may have superseded this request while it was in
    // flight; if so, this result belongs to a stale identity and must
    // never overwrite current state.
    if (userIdRef.current !== nextUserId) return;

    if (!result.ok) {
      lastFetchedAtRef.current = null;
      setCapabilities(EMPTY_SET);
      setError(result.error || "platform_security_capability_check_failed");
      setStatus("error");
      return;
    }

    // The server-resolved identity must exactly match the identity this
    // request was issued for. A mismatch (including a missing result.userId)
    // means the returned capability set cannot be trusted for this identity
    // and must never be accepted or exposed.
    if (result.userId !== nextUserId) {
      lastFetchedAtRef.current = null;
      setCapabilities(EMPTY_SET);
      setError("platform_security_capability_identity_mismatch");
      setStatus("error");
      return;
    }

    lastFetchedAtRef.current = Date.now();
    setCapabilities(new Set(result.capabilities));
    setError(null);
    setStatus("ready");
  }, []);

  const handleAuthEvent = useCallback(
    (_event, session) => {
      if (!session?.user?.id) {
        clearToUnauthenticated();
        return;
      }
      resolveCapabilities({ force: false });
    },
    [clearToUnauthenticated, resolveCapabilities]
  );

  const refresh = useCallback(() => {
    return resolveCapabilities({ force: true });
  }, [resolveCapabilities]);

  useEffect(() => {
    mountedRef.current = true;
    resolveCapabilities({ force: false });
    return () => {
      mountedRef.current = false;
    };
  }, [resolveCapabilities]);

  useEffect(() => {
    const { data: subscription } = supabase.auth.onAuthStateChange(
      handleAuthEvent
    );
    return () => {
      subscription?.subscription?.unsubscribe?.();
    };
  }, [handleAuthEvent]);

  const hasCapability = useCallback(
    (code) => {
      if (status !== "ready") return false;
      if (typeof code !== "string") return false;
      const normalized = code.trim();
      if (!normalized) return false;
      return capabilities.has(normalized);
    },
    [status, capabilities]
  );

  const value = useMemo(
    () => ({ status, capabilities, hasCapability, error, refresh }),
    [status, capabilities, hasCapability, error, refresh]
  );

  return (
    <PlatformSecurityCapabilityContext.Provider value={value}>
      {children}
    </PlatformSecurityCapabilityContext.Provider>
  );
}

export function usePlatformSecurityCapabilities() {
  const context = useContext(PlatformSecurityCapabilityContext);
  if (!context) {
    throw new Error(
      "usePlatformSecurityCapabilities must be used within a PlatformSecurityCapabilityProvider"
    );
  }
  return context;
}
